import { useQuery } from 'react-query';

import { useRequest, queryClient, type QueryFunction } from '@frontend/api';

import type { ActionDownfilterParams } from './types';

type GetActionsGroupedByRuleParams = {
  groupBy: 'quote_item' | 'component';
};

export type ActionsGroupedByRuleQueryParams = ActionDownfilterParams &
  GetActionsGroupedByRuleParams &
  (
    | {
        quoteItemId: string;
      }
    | {
        quoteId: string;
      }
  );

// The ID is the quoteItemId of the parent quote item
export type GroupedByRuleParentItem = {
  id: number;
  countObjects: number;
  childIds: number[];
};

export type ActionsGroupedByRuleResponseType = {
  rule: {
    uuid: string;
    name: string;
  };
  parentItems: GroupedByRuleParentItem[];
};

export const getActionsGroupedByRuleKey = (
  params: ActionsGroupedByRuleQueryParams,
) => ['actions', 'groupedByRule', JSON.stringify(params)];

export const useGetActionsGroupedByRule: QueryFunction<
  ActionsGroupedByRuleResponseType[],
  [ActionsGroupedByRuleQueryParams],
  ReturnType<typeof getActionsGroupedByRuleKey>
> = (params, { queryOptions, requestOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getActionsGroupedByRuleKey(params),
    queryFn: ({ signal }) =>
      request<ActionsGroupedByRuleResponseType[]>(
        '/rules/actions/grouped_by_rule/',
        {
          query: params,
          init: { signal },
        },
      ),
  });
};

export const invalidateActionsGroupedByRuleQuery = (
  params: ActionsGroupedByRuleQueryParams,
) => {
  queryClient.invalidateQueries(getActionsGroupedByRuleKey(params));
};
